import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Send, MessageCircle, Bug, Sparkles } from "lucide-react";
import BrainLogo from "@/components/BrainLogo";
import LegalLinks from "@/components/LegalLinks";
import api from "@/lib/api";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const TOPICS = [
  { value: "support", label: "I need help", icon: MessageCircle, color: "#7C5CFF" },
  { value: "bug", label: "Report a bug", icon: Bug, color: "#FCA5A5" },
  { value: "feature", label: "Suggest a feature", icon: Sparkles, color: "#B8A4FF" },
  { value: "billing", label: "Billing", icon: Mail, color: "#5AC8FA" },
  { value: "other", label: "Other", icon: Mail, color: "#8E8B99" },
];

export default function Contact() {
  const [topic, setTopic] = useState("support");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !message.trim()) {
      toast.error("Email and message are required");
      return;
    }
    if (message.trim().length < 10) {
      toast.error("Message is too short");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", {
        topic,
        name: name.trim() || null,
        email: email.trim(),
        message: message.trim(),
      });
      setSent(true);
      toast.success("Got it — we'll get back to you within 48 hours");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — try again later");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-[#F4F4F5]" data-testid="contact-page">
      <Seo title="Contact us" description="Get in touch with the Studivault team — questions, bug reports, feature ideas or billing help. We reply within 48 hours." path="/contact" />
      {/* Ambient glow */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none -z-10"
        style={{
          background:
            "radial-gradient(900px 480px at 22% 0%, rgba(124,92,255,0.18), transparent 60%), radial-gradient(700px 400px at 80% 30%, rgba(90,200,250,0.10), transparent 60%)",
        }}
      />

      <header className="border-b border-white/5 px-5 md:px-8 py-4">
        <div className="max-w-[1100px] mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="contact-home-link">
            <BrainLogo size={26} />
            <span className="font-semibold tracking-tight">Studivault</span>
          </Link>
          <Link to="/" className="text-[12.5px] text-[#B0AEBC] hover:text-white flex items-center gap-1">
            <ArrowLeft size={13} /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-5 md:px-8 py-12 md:py-20 grid md:grid-cols-[1.05fr_1.4fr] gap-12 md:gap-16 items-start">
        {/* Left — copy */}
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.45 }}
        >
          <div className="eyebrow mb-3">CONTACT</div>
          <h1 className="display-2 mb-5">Talk to us.</h1>
          <p className="body-lg text-[#B0AEBC] mb-7 max-w-md">
            Got a bug, a feature wish, or a billing question? Send a note — a real human
            replies within 48 hours, usually much sooner.
          </p>

          <div className="space-y-3.5 text-[13.5px]">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-[#B8A4FF] mt-0.5">
                <Mail size={15} />
              </div>
              <div>
                <div className="font-semibold text-white">hello@studivault.com</div>
                <div className="text-[#8E8B99] text-[12.5px] mt-0.5">For anything not urgent.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-[#5AC8FA] mt-0.5">
                <Bug size={15} />
              </div>
              <div>
                <div className="font-semibold text-white">Found a bug?</div>
                <div className="text-[#8E8B99] text-[12.5px] mt-0.5">
                  Tell us the URL, what you expected and what actually happened — we&apos;ll have it fixed.
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-[#7C5CFF] mt-0.5">
                <Sparkles size={15} />
              </div>
              <div>
                <div className="font-semibold text-white">Feature ideas</div>
                <div className="text-[#8E8B99] text-[12.5px] mt-0.5">
                  We read every one. Big roadmap moments often start here.
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right — form */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="surface p-6 md:p-8 rounded-3xl"
        >
          {sent ? (
            <div className="text-center py-10" data-testid="contact-sent">
              <div className="inline-flex w-14 h-14 rounded-full items-center justify-center mb-5"
                   style={{ background: "linear-gradient(135deg,#7C5CFF,#5AC8FA)", boxShadow: "0 0 32px rgba(124,92,255,0.55)" }}>
                <Send size={22} className="text-white" />
              </div>
              <h2 className="text-[20px] font-semibold mb-2">Message received</h2>
              <p className="text-[13.5px] text-[#B0AEBC] max-w-sm mx-auto mb-8">
                We&apos;ll reply to <span className="text-white">{email}</span> within 48 hours.
                In the meantime, your studies are waiting.
              </p>
              <Link to="/" className="btn btn-ghost" data-testid="contact-back-home">Back to Studivault</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div>
                <label className="eyebrow text-[10.5px] mb-3 block">WHAT&apos;S THIS ABOUT?</label>
                <div className="flex flex-wrap gap-2">
                  {TOPICS.map((t) => {
                    const Icon = t.icon;
                    const active = topic === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setTopic(t.value)}
                        data-testid={`contact-topic-${t.value}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold transition-all border ${
                          active
                            ? "bg-white/10 text-white border-white/15"
                            : "bg-transparent text-[#B0AEBC] border-white/5 hover:bg-white/5 hover:text-white"
                        }`}
                        style={active ? { boxShadow: `0 0 18px ${t.color}55` } : {}}
                      >
                        <Icon size={12} style={{ color: t.color }} /> {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="eyebrow text-[10.5px] mb-2 block">NAME (OPTIONAL)</span>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-[14px] focus:outline-none focus:border-[#7C5CFF] transition-colors"
                    placeholder="Your name"
                    data-testid="contact-name"
                  />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10.5px] mb-2 block">EMAIL</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-[14px] focus:outline-none focus:border-[#7C5CFF] transition-colors"
                    placeholder="you@example.com"
                    data-testid="contact-email"
                  />
                </label>
              </div>

              <label className="block">
                <span className="eyebrow text-[10.5px] mb-2 block">MESSAGE</span>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={7}
                  className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-[14px] focus:outline-none focus:border-[#7C5CFF] transition-colors resize-y leading-[1.55]"
                  placeholder="Tell us what's on your mind. The more detail the better — page URL, what you expected, what actually happened, screenshots welcome via email."
                  data-testid="contact-message"
                />
                <div className="text-[11px] text-[#71717A] mt-1.5 flex justify-between">
                  <span>{message.length} / 2000</span>
                  <span>Markdown not supported.</span>
                </div>
              </label>

              <button
                type="submit"
                disabled={busy}
                className="btn btn-primary w-full py-3.5 text-[14px] disabled:opacity-60"
                data-testid="contact-submit"
              >
                {busy ? "Sending…" : (<>Send message <Send size={14} /></>)}
              </button>

              <p className="text-[11.5px] text-[#71717A] text-center">
                By sending, you agree to our <Link to="/terms" className="hover:text-[#B8A4FF]">Terms</Link>{" "}
                and <Link to="/privacy-policy" className="hover:text-[#B8A4FF]">Privacy policy</Link>.
              </p>
            </form>
          )}
        </motion.div>
      </main>

      <footer className="border-t border-white/5 px-5 md:px-8 py-8 mt-10">
        <div className="max-w-[1100px] mx-auto flex flex-col md:flex-row items-center justify-between gap-2 text-[12px] text-[#8E8B99]">
          <span>© 2026 Studivault</span>
          <LegalLinks />
        </div>
      </footer>
    </div>
  );
}
